import posthog from 'posthog-js';
import { nullSafeGet } from './DataAccessUtils';
import { getFixedFloatStringOrZero, stringifyLineItemValue } from './InvoiceUtils';
import {
	adaptParsedQuoteToFormData,
	excludeTaxFromQuoteFormData,
	isTaxExcludedFromQuotesByBuyer,
} from './QuoteDataAdapter';

// Shared helper to build a quote (proposal) payload from InvoiceFormBase-style
// form values. Mirrors buildInvoicePayloadFromFormValues but emits the proposal
// field names (single `tax` + `taxRate`, `totalBeforeTax`/`totalAfterTax`, and
// the `incurredCost*` fields) that the quote CRUD endpoints expect.
export interface BuildQuotePayloadOptions {
	workOrderId: string | number;
	currentUser: any;
	isBuyerUploaded: boolean;
	status: string;
	excludeTax?: boolean;
	baseMeta?: {
		supplierFacilityId?: any;
		requestForProposalId?: any;
		proposalPdfLink?: string;
		declineNotes?: string;
	};
}

const hasLineItems = (items: any) => items && Array.isArray(items) && items.length > 0;

export const buildQuotePayloadFromFormValues = (
	values: any,
	scope: string | null | undefined,
	options: BuildQuotePayloadOptions
) => {
	const { workOrderId, currentUser, isBuyerUploaded, status, excludeTax, baseMeta } = options;

	const laborLineItemsArray = values.laborLineItems || [];
	const laborLineItems = hasLineItems(laborLineItemsArray)
		? laborLineItemsArray.map((item: any) => ({
				roleType: item.roleType || '',
				numberOfTech: Number(item.numberOfTech || 0),
				seconds: Number(item.seconds || 0),
				hourlyRate: String(item.hourlyRate || 0),
				amount: String(item.amount || 0),
		  }))
		: [];

	const partsLineItemsArray = values.partsLineItems || [];
	const partsLineItems = hasLineItems(partsLineItemsArray)
		? partsLineItemsArray.map((item: any, index: number) => ({
				description: item.description || '',
				partId: item.partId || index + 1,
				partNumber: item.partNumber || '',
				unitType: item.unitType || '',
				quantity: stringifyLineItemValue(item.quantity, ''),
				unitPrice: stringifyLineItemValue(item.unitPrice, ''),
				amount: String(item.amount || 0),
		  }))
		: [];

	const equipmentLineItemsArray = values.equipmentLineItems || [];
	const equipmentLineItems = hasLineItems(equipmentLineItemsArray)
		? equipmentLineItemsArray.map((item: any, index: number) => ({
				description: item.description || '',
				equipmentId: item.equipmentId || index + 1,
				equipmentNumber: item.equipmentNumber || '',
				quantity: '1',
				unitType: 'each',
				unitPrice: stringifyLineItemValue(item.unitPrice, undefined),
				amount: String(item.amount || 0),
		  }))
		: [];

	const materialLineItems = [...partsLineItems, ...equipmentLineItems];

	const mapSimpleLineItems = (arr: any[]) =>
		hasLineItems(arr)
			? arr.map((item: any) => ({
					description: item.description || '',
					amount: String(item.amount || 0),
					...(item.id && { id: item.id }),
					...(item.unitPrice && { unitPrice: String(item.unitPrice) }),
			  }))
			: [];

	const travelLineItems = mapSimpleLineItems(values.travelLineItems || []);
	const freightLineItems = mapSimpleLineItems(values.freightLineItems || []);
	const miscLineItems = mapSimpleLineItems(values.miscLineItems || []);
	const incurredCostLineItems = mapSimpleLineItems(values.incurredLineItems || []);

	// CostSection writes `${fieldKey}Total` for each section it manages.
	const laborTotal = Number(values.laborTotal) || 0;
	const partsTotal = Number(values.partsTotal) || 0;
	const equipmentTotal = Number(values.equipmentTotal) || 0;
	const travelTotal = Number(values.travelTotal) || 0;
	const freightTotal = Number(values.freightTotal) || 0;
	const miscTotal = Number(values.miscTotal) || 0;
	const incurredTotal = Number(values.incurredTotal) || 0;

	const laborTotalBeforeTax = getFixedFloatStringOrZero(laborTotal);
	const materialTotalBeforeTax = getFixedFloatStringOrZero(partsTotal + equipmentTotal);
	const travelTotalBeforeTax = getFixedFloatStringOrZero(travelTotal);
	const freightTotalBeforeTax = getFixedFloatStringOrZero(freightTotal);
	const miscTotalBeforeTax = getFixedFloatStringOrZero(miscTotal);
	const incurredCostTotalBeforeTax = getFixedFloatStringOrZero(incurredTotal);

	// InvoiceFormBase tracks the single tax in `invoiceTax` and the grand totals
	// in `invoiceTotalBeforeTax` / `invoiceTotalAfterTax`.
	const tax = excludeTax ? 0 : Number(values.invoiceTax) || 0;
	const totalBeforeTax = Number(values.invoiceTotalBeforeTax) || 0;
	const totalAfterTax = excludeTax ? totalBeforeTax : Number(values.invoiceTotalAfterTax) || 0;
	const taxRate = totalBeforeTax > 0 ? (tax * 100.0) / totalBeforeTax : 0;

	const payload: any = {
		id: values.id,
		scope: scope !== null && scope !== undefined ? scope : '',
		status: values.status || status || 'draft',
		taxRate: `${taxRate}`,
		tax: getFixedFloatStringOrZero(tax),
		totalBeforeTax: getFixedFloatStringOrZero(totalBeforeTax),
		totalAfterTax: getFixedFloatStringOrZero(totalAfterTax),
		laborTotalBeforeTax,
		laborLineItems,
		materialTotalBeforeTax,
		materialLineItems,
		freightTotalBeforeTax,
		freightLineItems,
		travelTotalBeforeTax,
		travelLineItems,
		miscTotalBeforeTax,
		miscLineItems,
		incurredCostTotalBeforeTax,
		incurredCostLineItems,
		workOrderId: parseInt(String(workOrderId), 10),
		createdBy: currentUser.email,
		attachments: values.attachments || [],
		isBuyerUploaded,
	};

	if (baseMeta && baseMeta.supplierFacilityId !== undefined) {
		payload.supplierFacilityId = baseMeta.supplierFacilityId;
	}
	if (baseMeta && baseMeta.requestForProposalId !== undefined) {
		payload.requestForProposalId = baseMeta.requestForProposalId;
	}
	if (baseMeta && baseMeta.declineNotes !== undefined) {
		payload.declineNotes = baseMeta.declineNotes;
	}
	if (baseMeta && baseMeta.proposalPdfLink !== undefined) {
		payload.proposalPdfLink = baseMeta.proposalPdfLink;
	}

	return payload;
};

// Compare the parsed snapshot with the final submitted quote, for analytics.
// Mirrors compareParsedWithFinal but keyed on proposal field names and includes
// the incurred-cost category.
export const compareParsedQuoteWithFinal = (parsedSnapshot: any, finalQuote: any) => {
	if (!parsedSnapshot) {
		return null;
	}

	const changedFields: string[] = [];
	let totalFieldsChanged = 0;

	const normalizeValue = (val: any) => {
		if (val === null || val === undefined) return '';
		if (val instanceof Date) return val.toISOString();
		const num = parseFloat(String(val));
		if (!isNaN(num)) return num.toFixed(2);
		return String(val).trim();
	};

	const fieldsToCompare = ['tax', 'totalAfterTax'];
	fieldsToCompare.forEach((field) => {
		if (normalizeValue(parsedSnapshot[field]) !== normalizeValue(finalQuote[field])) {
			changedFields.push(field);
			totalFieldsChanged++;
		}
	});

	// The parsed snapshot (adaptParsedQuoteToFormData) keys totals by the form-field
	// name, while the final payload (buildQuotePayloadFromFormValues) uses the proposal
	// name. These match for every category except incurred, where the snapshot stores
	// `incurredTotalBeforeTax` but the payload stores `incurredCostTotalBeforeTax` — so
	// the two sides need independent keys, otherwise incurred always reads as edited.
	const categoryChanged = (snapshotKey: string, finalKey: string = snapshotKey) =>
		normalizeValue(parsedSnapshot[snapshotKey]) !== normalizeValue(finalQuote[finalKey]) ? 1 : 0;

	const laborChanged = categoryChanged('laborTotalBeforeTax');
	const materialChanged = categoryChanged('materialTotalBeforeTax');
	const travelChanged = categoryChanged('travelTotalBeforeTax');
	const freightChanged = categoryChanged('freightTotalBeforeTax');
	const miscChanged = categoryChanged('miscTotalBeforeTax');
	const incurredChanged = categoryChanged('incurredTotalBeforeTax', 'incurredCostTotalBeforeTax');

	const totalLineItemsChanged =
		laborChanged + materialChanged + travelChanged + freightChanged + miscChanged + incurredChanged;

	return {
		total_fields_changed: totalFieldsChanged,
		changed_fields: changedFields,
		labor_line_items_changed: laborChanged,
		material_line_items_changed: materialChanged,
		travel_line_items_changed: travelChanged,
		freight_line_items_changed: freightChanged,
		misc_line_items_changed: miscChanged,
		incurred_line_items_changed: incurredChanged,
		total_line_items_changed: totalLineItemsChanged,
	};
};

interface ApplyParsedQuoteToFormOptions {
	parsedData: any;
	workOrder: any;
	userType: string;
	setQuoteFormData: (updater: (prev: any) => any) => void;
	setParsedFormDataSnapshot: (snapshot: any) => void;
	setParsedDiscount: (discount: any) => void;
	setDisableCosts: (disabled: boolean) => void;
}

/**
 * Merges AI-parsed quote data into the V2 form state and emits the
 * `quote_pdf_parsed` PostHog event. Mirrors applyParsedInvoiceToForm. Does NOT
 * touch proposalPdfLink (arrives separately via onS3UrlReady) or the
 * currency-mismatch flow (use getCurrencyMismatch upstream).
 */
export const applyParsedQuoteToForm = (options: ApplyParsedQuoteToFormOptions) => {
	const {
		parsedData,
		workOrder,
		userType,
		setQuoteFormData,
		setParsedFormDataSnapshot,
		setParsedDiscount,
		setDisableCosts,
	} = options;

	if (!workOrder) {
		posthog.capture('quote_pdf_parsed', {
			user_type: userType,
			has_parsed_data: false,
			error: 'no_parsed_data',
		});
		return;
	}

	const parsedFormData = adaptParsedQuoteToFormData(parsedData, workOrder);
	if (!parsedFormData) {
		posthog.capture('quote_pdf_parsed', {
			user_type: userType,
			has_parsed_data: false,
			error: 'adaptation_failed',
		});
		return;
	}

	const adaptedFormData = isTaxExcludedFromQuotesByBuyer(workOrder)
		? excludeTaxFromQuoteFormData(parsedFormData)
		: parsedFormData;

	setQuoteFormData((prevFormData) => ({
		...prevFormData,
		...adaptedFormData,
		id: prevFormData.id,
		status: prevFormData.status,
		supplierFacilityId: prevFormData.supplierFacilityId,
	}));
	setParsedFormDataSnapshot(adaptedFormData);

	const discount = nullSafeGet('discount', parsedData);
	setParsedDiscount(discount);
	setDisableCosts((discount || 0) > 0);

	const hasDiscount = (discount || 0) > 0;
	posthog.capture('quote_pdf_parsed', {
		user_type: userType,
		has_parsed_data: true,
		has_discount: hasDiscount,
		...(hasDiscount && discount != null && { discount }),
	});
};
